import { Label } from "@/components/ui/field";
import { Heading } from "@/components/ui/heading";
import { NumberField, NumberInput } from "@/components/ui/number-field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import type { LabelSettings } from "../types";

interface LabelDimensionsProps {
  settings: LabelSettings;
  onChange: (settings: LabelSettings) => void;
}

const PRESET_SIZES = [
  { name: '2" x 1"', widthInches: 2, heightInches: 1 },
  { name: '2.25" x .75"', widthInches: 2.25, heightInches: 0.75 },
  { name: '4" x 2"', widthInches: 4, heightInches: 2 },
  { name: '4" x 3"', widthInches: 4, heightInches: 3 },
  { name: '3" x 2"', widthInches: 3, heightInches: 2 },
  { name: "Custom", widthInches: 0, heightInches: 0 },
];

const DPI_OPTIONS = [
  { value: 152, label: "152 DPI (6 DPMM)" },
  { value: 203, label: "203 DPI (8 DPMM)" },
  { value: 300, label: "300 DPI (12 DPMM)" },
  { value: 600, label: "600 DPI (24 DPMM)" },
];

// Common format options for dimension inputs
const DIMENSION_FORMAT_OPTIONS = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
} as const;

// Helper functions
const inchesToDots = (inches: number, dpi: number): number =>
  Math.floor(inches * dpi);
const dotsToInches = (dots: number, dpi: number): number =>
  Number((dots / dpi).toFixed(3));

const findPresetByDimensions = (width: number, height: number, dpi: number) => {
  return PRESET_SIZES.find((preset) => {
    if (preset.name === "Custom") return false;
    const expectedWidth = inchesToDots(preset.widthInches, dpi);
    const expectedHeight = inchesToDots(preset.heightInches, dpi);
    return expectedWidth === width && expectedHeight === height;
  });
};

export function LabelDimensions({ settings, onChange }: LabelDimensionsProps) {
  // Convert dots to inches for display
  const widthInches = dotsToInches(settings.width, settings.dpi);
  const heightInches = dotsToInches(settings.height, settings.dpi);

  const handlePresetChange = (preset: string) => {
    const size = PRESET_SIZES.find((s) => s.name === preset);
    if (size && size.name !== "Custom") {
      onChange({
        ...settings,
        width: inchesToDots(size.widthInches, settings.dpi),
        height: inchesToDots(size.heightInches, settings.dpi),
      });
    }
  };

  // Find current preset by checking if dimensions match any preset at current DPI
  const currentPreset =
    findPresetByDimensions(settings.width, settings.height, settings.dpi)
      ?.name || "Custom";

  const handleDimensionChange =
    (field: "width" | "height") => (inches: number) => {
      onChange({ ...settings, [field]: inchesToDots(inches, settings.dpi) });
    };

  const handleDpiChange = (key: string | number | null) => {
    if (key === null) {
      onChange({ ...settings, dpi: 0 });
      return;
    }
    const newDpi = Number.parseInt(key.toString(), 10);
    const newSettings = { ...settings, dpi: newDpi };

    // If a preset is currently selected, recalculate dimensions for new DPI
    const selectedPreset = findPresetByDimensions(
      settings.width,
      settings.height,
      settings.dpi,
    );
    if (selectedPreset) {
      newSettings.width = inchesToDots(selectedPreset.widthInches, newDpi);
      newSettings.height = inchesToDots(selectedPreset.heightInches, newDpi);
    }

    onChange(newSettings);
  };

  const handleMarginChange =
    (
      field: keyof Pick<
        LabelSettings,
        "marginLeft" | "marginRight" | "marginTop" | "marginBottom"
      >,
    ) =>
    (value: number) =>
      onChange({ ...settings, [field]: value });

  return (
    <div className="space-y-4">
      <Heading level={3}>Label Dimensions</Heading>

      <Select
        selectedKey={currentPreset}
        onSelectionChange={(key) => handlePresetChange(key as string)}
      >
        <Label>Preset Sizes</Label>
        <SelectTrigger />
        <SelectList>
          {PRESET_SIZES.map((size) => (
            <SelectItem key={size.name} id={size.name}>
              {size.name}
            </SelectItem>
          ))}
        </SelectList>
      </Select>

      <div className="grid grid-cols-2 gap-2">
        <NumberField
          value={widthInches}
          onChange={handleDimensionChange("width")}
          minValue={0.5}
          maxValue={12}
          step={0.125}
          formatOptions={DIMENSION_FORMAT_OPTIONS}
        >
          <Label>Width (inches)</Label>
          <NumberInput />
        </NumberField>
        <NumberField
          value={heightInches}
          onChange={handleDimensionChange("height")}
          minValue={0.25}
          maxValue={12}
          step={0.125}
          formatOptions={DIMENSION_FORMAT_OPTIONS}
        >
          <Label>Height (inches)</Label>
          <NumberInput />
        </NumberField>
      </div>

      <Select
        selectedKey={settings.dpi.toString()}
        onSelectionChange={handleDpiChange}
      >
        <Label>DPI</Label>
        <SelectTrigger />
        <SelectList>
          {DPI_OPTIONS.map((option) => (
            <SelectItem key={option.value} id={option.value.toString()}>
              {option.label}
            </SelectItem>
          ))}
        </SelectList>
      </Select>

      <div className="space-y-2">
        <Label className="text-sm font-medium">Print Margins (dots)</Label>
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            value={settings.marginLeft || 8}
            onChange={handleMarginChange("marginLeft")}
            minValue={0}
            maxValue={50}
          >
            <Label>Left</Label>
            <NumberInput />
          </NumberField>
          <NumberField
            value={settings.marginRight || 8}
            onChange={handleMarginChange("marginRight")}
            minValue={0}
            maxValue={50}
          >
            <Label>Right</Label>
            <NumberInput />
          </NumberField>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            value={settings.marginTop || 8}
            onChange={handleMarginChange("marginTop")}
            minValue={0}
            maxValue={50}
          >
            <Label>Top</Label>
            <NumberInput />
          </NumberField>
          <NumberField
            value={settings.marginBottom || 8}
            onChange={handleMarginChange("marginBottom")}
            minValue={0}
            maxValue={50}
          >
            <Label>Bottom</Label>
            <NumberInput />
          </NumberField>
        </div>
      </div>
    </div>
  );
}
